import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
    kindLabel, surveyTypeLabel, responseTypeLabel, Rights, ResponseTypes, MediaTypes, SurveyTypes,
    isMediaResponse, languageLabel
} from '@aiinterview/shared';
import { api } from '../api/client';
import { useSession } from '../api/session';
import { Card, Loading, ErrorNote, EmptyState, StatusPill } from '../components/ui';
import { Avatar } from '../components/Avatar';

/**
 * One employee's answers, laid out the way an interview report is so the two read alike.
 *
 * What is deliberately missing is as much of the point as what is here: no score, no verdict and
 * nothing to approve. Somebody reads this, and that is the whole reason it was collected. Marking
 * feedback changes what people are willing to say into it.
 */
interface Answer {
    id: number;
    sequence: number;
    questionText: string;
    transcriptText: string | null;
    translatedTranscript: string | null;
    durationSeconds: number;
    wordCount: number;
    noAnswerGiven: boolean;
    responseType: string;
    selectedOptions: string[];
    offeredOptions: string[];
    mediaType: string | null;
    hasMedia: boolean;
}

interface Detail {
    id: number;
    kind: string;
    employeeCode: string;
    role: string | null;
    region: string | null;
    branch: string | null;
    state: string | null;
    status: string;
    language: string | null;
    aiSummary: string | null;
    startedAt: string | null;
    completedAt: string | null;
    durationSeconds: number | null;
    totalQuestions: number;
    recordingConsented: boolean;
    mediaChoice: string | null;
    format: {
        name: string; kind: string; description: string | null; surveyType: string;
        mediaType: string | null;
    };
    answers: Answer[];
}

const mediaUrl = (id: number, sequence: number): string =>
    `/api/admin/interactions/${id}/media/${sequence}`;

/**
 * A chosen list, with what was left unticked beneath it.
 *
 * What somebody declined to choose is part of their answer. A bare list of ticks hides it, and two
 * responses that ticked "Scheduling" mean different things if one of them also had "Spare parts"
 * on offer and passed it over.
 */
function ChoiceAnswer({ answer }: { answer: Answer }) {
    const passedOver = answer.offeredOptions.filter((o) => !answer.selectedOptions.includes(o));

    return (
        <>
            {answer.selectedOptions.length === 0 ? (
                <span style={{ color: 'var(--text-muted-safe)' }}>Nothing was chosen.</span>
            ) : (
                <ul className="space-y-1">
                    {answer.selectedOptions.map((option) => (
                        <li key={option} className="flex items-start gap-2">
                            <i className="bi bi-check2 mt-0.5" aria-hidden="true" />
                            <span>{option}</span>
                        </li>
                    ))}
                </ul>
            )}

            {passedOver.length > 0 && (
                <ul className="mt-2 space-y-1" style={{ color: 'var(--text-muted-safe)' }}>
                    {passedOver.map((option) => (
                        <li key={option} className="flex items-start gap-2 text-xs">
                            <i className="bi bi-dash mt-0.5" aria-hidden="true" />
                            <span>{option}</span>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return '—';
    const minutes = Math.floor(seconds / 60);
    return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

const formatWhen = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString() : '—';

export function InteractionReport() {
    const { id } = useParams();
    const { can } = useSession();

    const { data, isLoading, error } = useQuery({
        queryKey: ['interaction', id],
        queryFn: () => api.get<Detail>(`/api/admin/interactions/${id}`)
    });

    if (isLoading) {
        return <Loading what="Opening" />;
    }

    if (error || !data) {
        return <ErrorNote message={(error as Error)?.message ?? 'That response no longer exists.'} />;
    }

    const listPath = data.kind === 'Survey' ? '/surveys' : '/listening';

    /** Blank fields are shown as a dash rather than dropped: a gap is itself worth seeing. */
    const details: [string, string][] = [
        ['Role', data.role ?? '—'],
        ['Region', data.region ?? '—'],
        ['Branch', data.branch ?? '—'],
        ['State', data.state ?? '—']
    ];

    const given = data.answers.filter((a) => !a.noAnswerGiven);
    const words = given.reduce((total, a) => total + a.wordCount, 0);
    const isForm = data.format.surveyType === SurveyTypes.Form;

    return (
        <div className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
                <Card>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <Avatar name={data.employeeCode} />
                            <div>
                                <h1 className="app-page-heading app-mono">{data.employeeCode}</h1>
                                <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                    {data.format.name}
                                    {data.language && ` · answered in ${languageLabel(data.language)}`}
                                </p>
                                <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                    <Link to={listPath} className="underline">
                                        All {kindLabel(data.kind).toLowerCase()} responses
                                    </Link>
                                </p>
                            </div>
                        </div>

                        <StatusPill kind="status" value={data.status} />
                    </div>

                    <div className="report-stats">
                        <div>
                            <div className="report-stat-label">Answered</div>
                            <div className="report-stat-value">
                                {data.answers.length} of {data.totalQuestions}
                            </div>
                        </div>
                        <div>
                            {/* Answered counts questions reached; this counts the ones they actually
                                gave something to, which is the number worth reading a survey by. */}
                            <div className="report-stat-label">Skipped</div>
                            <div className="report-stat-value">
                                {data.answers.length - given.length}
                            </div>
                        </div>
                        {/* Words mean nothing on a questionnaire of tick lists, so a form does not
                            claim a number for them. */}
                        {!isForm && (
                            <div>
                                <div className="report-stat-label">Words</div>
                                <div className="report-stat-value">{words}</div>
                            </div>
                        )}
                        <div>
                            <div className="report-stat-label">Took</div>
                            <div className="report-stat-value">{formatDuration(data.durationSeconds)}</div>
                        </div>
                        <div>
                            <div className="report-stat-label">Finished</div>
                            <div className="report-stat-value">{formatWhen(data.completedAt)}</div>
                        </div>
                    </div>

                    {data.status !== 'Completed' && (
                        <p
                            className="mt-4 rounded-lg px-3 py-2 text-sm"
                            style={{ background: '#fef3c7', color: '#92400e' }}
                        >
                            {/* Stated rather than left to be inferred from a short list. Half an
                                answer set is not the same evidence as a finished one. */}
                            This was not finished. Everything said up to that point is below;
                            the remaining questions were never asked.
                        </p>
                    )}

                    {/* Written by the model once the response was complete, and absent whenever it
                        was not - an unconfigured model, a failed call, or a response older than
                        this. Nothing stands in for it, because a placeholder would read as a
                        finding about the employee rather than about the setup. */}
                    {data.aiSummary && (
                        <div
                            className="mt-4 border-t pt-4"
                            style={{ borderColor: 'var(--app-border-soft)' }}
                        >
                            <div className="report-stat-label">Summary</div>
                            <p className="mt-1 whitespace-pre-wrap text-sm">{data.aiSummary}</p>
                        </div>
                    )}
                </Card>

                <Card title="Who answered">
                    <dl className="space-y-3">
                        <div>
                            <dt className="report-stat-label">Employee code</dt>
                            <dd className="report-stat-value app-mono">{data.employeeCode}</dd>
                        </div>
                        {details.map(([label, value]) => (
                            <div key={label}>
                                <dt className="report-stat-label">{label}</dt>
                                <dd className="report-stat-value">{value}</dd>
                            </div>
                        ))}
                        <div>
                            <dt className="report-stat-label">Started</dt>
                            <dd className="report-stat-value">{formatWhen(data.startedAt)}</dd>
                        </div>
                        <div>
                            <dt className="report-stat-label">Type</dt>
                            <dd className="report-stat-value">
                                {data.kind === 'Listening'
                                    ? 'Spoken conversation'
                                    : surveyTypeLabel(data.format.surveyType)}
                                {data.mediaChoice && (
                                    <span className="ml-2 text-xs text-slate-500">
                                        {responseTypeLabel(data.mediaChoice).toLowerCase()}
                                    </span>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="report-stat-label">Recording</dt>
                            {/* Consent is required to start, so "not agreed" only ever describes a
                                response from before that rule, and says so rather than implying
                                somebody was recorded without being asked. */}
                            <dd className="report-stat-value">
                                {data.recordingConsented ? 'Agreed to' : 'Not on record'}
                            </dd>
                        </div>
                    </dl>

                    <p
                        className="mt-4 border-t pt-4 text-xs"
                        style={{ borderColor: 'var(--app-border-soft)', color: 'var(--text-muted-safe)' }}
                    >
                        {/* Says plainly why the panel a reader expects from an interview report is
                            not here, rather than leaving them to wonder whether it failed. */}
                        Nothing here is scored. A {kindLabel(data.kind).toLowerCase()} is collected
                        and read, not marked.
                        {data.recordingConsented
                            && ' Recordings are removed once they pass the retention period; the'
                               + ' written answers are kept.'}
                    </p>
                </Card>
            </div>

            <Card title="What they said">
                {data.answers.length === 0 ? (
                    <EmptyState message="Nothing was answered." />
                ) : (
                    <div className="space-y-5">
                        {data.answers.map((answer) => (
                            <article
                                key={answer.id}
                                className="rounded-lg border p-4"
                                style={{ borderColor: 'var(--app-border)' }}
                            >
                                <header>
                                    <span className="text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                        Question {answer.sequence}
                                        {/* What was asked for, so a missing clip reads as a missing
                                            clip rather than a missing answer. */}
                                        {' · '}{responseTypeLabel(answer.responseType)}
                                    </span>
                                    <h3
                                        className="mt-0.5 text-sm font-medium"
                                        style={{ color: 'var(--app-heading)' }}
                                    >
                                        {answer.questionText}
                                    </h3>
                                </header>

                                <div
                                    className="mt-3 rounded-lg p-3 text-sm"
                                    style={{ background: 'var(--app-bg)' }}
                                >
                                    {answer.responseType === ResponseTypes.Checklist
                                        ? <ChoiceAnswer answer={answer} />
                                        : answer.noAnswerGiven || !answer.transcriptText
                                            ? (
                                                <span style={{ color: 'var(--text-muted-safe)' }}>
                                                    {isMediaResponse(answer.responseType)
                                                        ? 'Nothing recognisable was said.'
                                                        : 'Nothing was written.'}
                                                </span>
                                            )
                                            : (
                                                // Falls back to the original when there is no
                                                // translation, which is every English response
                                                // and everything answered before this existed.
                                                <p className="whitespace-pre-wrap">
                                                    {answer.translatedTranscript ?? answer.transcriptText}
                                                </p>
                                            )}
                                </div>

                                {answer.translatedTranscript && answer.transcriptText && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                            Show what the employee actually said
                                        </summary>
                                        <p
                                            className="mt-2 whitespace-pre-wrap rounded-lg p-3 text-sm"
                                            style={{ background: 'var(--app-bg)' }}
                                        >
                                            {answer.transcriptText}
                                        </p>
                                    </details>
                                )}

                                {/* Three states, none of them an empty player: a clip you may
                                    play, a clip you may not, and no clip at all. */}
                                {answer.hasMedia && (can(Rights.ViewRecording) ? (
                                    answer.mediaType === MediaTypes.Video ? (
                                        <video
                                            controls
                                            preload="none"
                                            className="mt-3 w-full rounded"
                                            style={{ maxHeight: '320px', background: '#000' }}
                                            src={mediaUrl(data.id, answer.sequence)}
                                        />
                                    ) : (
                                        <audio
                                            controls
                                            preload="none"
                                            className="mt-3 w-full"
                                            src={mediaUrl(data.id, answer.sequence)}
                                        />
                                    )
                                ) : (
                                    <p className="mt-3 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                        A recording of this answer exists. You do not have permission
                                        to play it.
                                    </p>
                                ))}

                                <dl
                                    className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs"
                                    style={{ color: 'var(--text-muted-safe)' }}
                                >
                                    {answer.durationSeconds > 0 && (
                                        <span>{answer.durationSeconds} s</span>
                                    )}
                                    {answer.responseType !== ResponseTypes.Checklist && (
                                        <span>{answer.wordCount} words</span>
                                    )}
                                    {isMediaResponse(answer.responseType) && !answer.hasMedia && (
                                        <span>no recording kept</span>
                                    )}
                                </dl>
                            </article>
                        ))}

                        {data.answers.length < data.totalQuestions && (
                            <p className="text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                {data.totalQuestions - data.answers.length} question(s) were never
                                reached.
                            </p>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
